if (!customElements.get("product-card")) {
  customElements.define(
    "product-card",
    class ProductCard extends HTMLElement {
      connectedCallback() {
        this.initCarousel();
        this.initSwatches();
        this.initDropdowns();
      }

      // Uses event delegation on the swatch container; updates variant image, price and button on selection
      initSwatches() {
        if (!this.swatchContainer) return;

        this.swatchContainer.addEventListener("click", async (event) => {
          const button = event.target.closest("[data-card-swatch]");
          if (!button) return;

          if (this.isReplacingCard) return;

          this.swatchContainer
            .querySelector(".card-swatch--active")
            ?.classList.remove("card-swatch--active");
          this.swatchContainer
            .querySelector('[aria-checked="true"]')
            ?.setAttribute("aria-checked", "false");
          button.classList.add("card-swatch--active");
          button.setAttribute("aria-checked", "true");

          if (button.dataset.isLinkedProduct === "true") {
            await this.replaceCardFromLinkedSwatch(button);
            return;
          }

          this.updateFromSelections();
        });
      }

      // Fetches a fully rendered product card for linked products and swaps this card in one operation
      async replaceCardFromLinkedSwatch(button) {
        const productUrl = button.dataset.productUrl;
        if (!productUrl) return;

        this.isReplacingCard = true;
        this.classList.add("card-product--loading");

        try {
          const sectionUrl = this.buildCardSectionUrl(productUrl);
          const response = await fetch(sectionUrl, {
            headers: {
              "X-Requested-With": "XMLHttpRequest",
            },
          });

          if (!response.ok) {
            throw new Error("Failed to fetch product card section");
          }

          const html = await response.text();
          const fragment = document.createElement("div");
          fragment.innerHTML = html;

          const cardType = this.dataset.cardType || "normal";
          const nextCard =
            fragment.querySelector(
              `product-card[data-card-type="${cardType}"]`,
            ) || fragment.querySelector("product-card");

          if (!nextCard) {
            throw new Error("No product-card found in section response");
          }

          this.applyCardOptions(nextCard);
          this.replaceWith(nextCard);

          // Let other scripts react to card replacement (quick-add custom element rebinds on connect).
          document.dispatchEvent(
            new CustomEvent("product-card:replaced", {
              detail: { card: nextCard },
            }),
          );

          window.swymCollectionsLoaded?.();
        } catch (error) {
          this.classList.remove("card-product--loading");
          this.isReplacingCard = false;
          this.updateFromSelections();
        }
      }

      applyCardOptions(card) {
        const options = {
          cardType: this.dataset.cardType || "normal",
          showOneSize: this.dataset.showOneSize || "false",
          hideCta: this.dataset.hideCta || "false",
          showWishlist: this.dataset.showWishlist || "false",
          showDescription: this.dataset.showDescription || "false",
          hybridShopLook: this.dataset.hybridShopLook || "false",
        };

        if (options.showWishlist !== "true") {
          card.querySelector('quick-add[data-mode="wishlist"]')?.remove();
        }

        if (options.hideCta === "true") {
          card
            .querySelectorAll(".card-product__button")
            .forEach((button) => button.remove());
        }

        if (options.showDescription !== "true") {
          card.querySelector(".card-product__description")?.remove();
        }

        if (options.showOneSize !== "true") {
          card
            .querySelector('select[id$="-one-size"]')
            ?.closest(".card-product__dropdown")
            ?.remove();
        }

        Object.entries(options).forEach(([key, value]) => {
          card.dataset[key] = value;
        });
      }

      // Builds section-rendering URL for the target product
      //
      // Note: Shopify's Section Rendering API doesn't forward arbitrary
      // custom query params into Liquid's request.params for section_id
      // fetches (only a small reserved set like q/page/variant). So the
      // hybrid shop-look state can't be passed as a query param — instead
      // we pick between two section files via section_id itself, which
      // Shopify always respects since that's how the endpoint routes.
      buildCardSectionUrl(productUrl) {
        const url = new URL(productUrl, window.location.origin);
        const isHybridShopLook = this.dataset.hybridShopLook === "true";

        url.searchParams.set(
          "section_id",
          isHybridShopLook
            ? "product-card-render-hybrid"
            : "product-card-render",
        );
        url.searchParams.set("card_type", this.dataset.cardType || "normal");
        url.searchParams.set(
          "show_one_size",
          this.dataset.showOneSize || "false",
        );
        url.searchParams.set("hide_cta", this.dataset.hideCta || "false");
        url.searchParams.set(
          "show_wishlist",
          this.dataset.showWishlist || "false",
        );
        url.searchParams.set(
          "show_description",
          this.dataset.showDescription || "false",
        );

        return `${url.pathname}${url.search}${url.hash}`;
      }

      // Initialises change listeners on dropdown selects and validates all options
      initDropdowns() {
        if (!this.dropdownContainer) return;

        this.updateDropdownAvailability();

        this.dropdownContainer.addEventListener("change", (event) => {
          if (event.target.matches("select[data-card-option]")) {
            this.updateFromSelections();
          }
        });
      }

      // Gathers all selected option values and finds the matching variant
      updateFromSelections() {
        this.updateDropdownAvailability();

        const matchedVariant = this.findMatchingVariant();
        this.validateSelections(matchedVariant);

        if (!matchedVariant || !matchedVariant.available) return;

        this.updateImage(matchedVariant);
        this.updatePrice(matchedVariant);
        this.updateVariantId(matchedVariant);
        this.updateProductLinks(matchedVariant);
        this.updateQuickAddUrl(matchedVariant);
      }

      // Updates all product links in the card to include the selected variant query param
      updateProductLinks(variant) {
        if (!variant?.id || !this.productLinks.length) return;

        this.productLinks.forEach((link) => {
          const href = link.getAttribute("href");
          if (!href) return;

          const url = new URL(href, window.location.origin);
          url.searchParams.set("variant", variant.id);

          const isAbsolute = /^(https?:)?\/\//i.test(href);
          const nextHref = isAbsolute
            ? url.toString()
            : `${url.pathname}${url.search}${url.hash}`;

          link.setAttribute("href", nextHref);
        });
      }

      // Updates quick-add data-product-url to include the selected variant query param
      updateQuickAddUrl(variant) {
        if (!variant?.id) return;

        this.quickAddButtons.forEach((button) => {
          const baseUrl = button.dataset.productUrl;
          if (!baseUrl) return;

          const url = new URL(baseUrl, window.location.origin);
          url.searchParams.set("variant", variant.id);

          const isAbsolute = /^(https?:)?\/\//i.test(baseUrl);
          const nextUrl = isAbsolute
            ? url.toString()
            : `${url.pathname}${url.search}${url.hash}`;

          button.dataset.productUrl = nextUrl;
        });
      }

      // Returns all currently selected option values in order
      getSelectedOptions() {
        const selectedByPosition = this.getSelectedOptionsByPosition();

        return Object.entries(selectedByPosition)
          .sort((a, b) => Number(a[0]) - Number(b[0]))
          .map(([, value]) => value)
          .filter(Boolean);
      }

      // Returns selected values keyed by option position (option1, option2, ...)
      getSelectedOptionsByPosition() {
        const selected = {};

        const activeSwatch = this.swatchContainer?.querySelector(
          ".card-swatch--active",
        );
        if (activeSwatch?.dataset.cardOption && activeSwatch.dataset.value) {
          selected[Number(activeSwatch.dataset.cardOption)] =
            activeSwatch.dataset.value;
        }

        const dropdowns = this.dropdownContainer?.querySelectorAll(
          "select[data-card-option]",
        );

        if (dropdowns) {
          Array.from(dropdowns).forEach((select) => {
            const position = Number(select.dataset.cardOption);
            if (position && select.value) {
              selected[position] = select.value;
            }
          });
        }

        return selected;
      }

      // Disables dropdown values that cannot resolve to an in-stock variant for the current selection
      updateDropdownAvailability() {
        if (!this.dropdownContainer) return;

        const selects = Array.from(
          this.dropdownContainer.querySelectorAll("select[data-card-option]"),
        ).sort(
          (a, b) => Number(a.dataset.cardOption) - Number(b.dataset.cardOption),
        );

        selects.forEach((select) => {
          const currentPosition = Number(select.dataset.cardOption);
          const selectedByPosition = this.getSelectedOptionsByPosition();

          Array.from(select.options).forEach((option) => {
            if (!option.value) return;

            const isAvailable = this.variantsData.some((variant) => {
              if (!variant.available) return false;
              if (variant[`option${currentPosition}`] !== option.value) {
                return false;
              }

              return Object.entries(selectedByPosition).every(
                ([position, selectedValue]) => {
                  const optionPosition = Number(position);
                  if (!selectedValue || optionPosition === currentPosition) {
                    return true;
                  }

                  return variant[`option${optionPosition}`] === selectedValue;
                },
              );
            });

            option.disabled = !isAvailable;
          });

          if (select.value && select.selectedOptions[0]?.disabled) {
            select.value = "";
          }
        });
      }

      // Finds the variant matching all selected options (prefers available)
      findMatchingVariant() {
        const selectedByPosition = this.getSelectedOptionsByPosition();
        const selectedEntries = Object.entries(selectedByPosition);
        if (selectedEntries.length === 0) return null;

        const variants = this.variantsData;

        const isMatch = (variant) =>
          selectedEntries.every(
            ([position, value]) => variant[`option${position}`] === value,
          );

        return (
          variants.find((variant) => isMatch(variant) && variant.available) ||
          variants.find((variant) => isMatch(variant))
        );
      }

      // Checks if all swatches and dropdowns have a selection, and toggles the add-to-cart button
      validateSelections(matchedVariant = null) {
        if (!this.addToCartButton) return;

        const swatchSelected =
          !this.swatchContainer ||
          this.swatchContainer.querySelector(".card-swatch--active");
        const dropdowns = this.dropdownContainer?.querySelectorAll(
          "select[data-card-option]",
        );
        const allDropdownsSelected =
          !dropdowns ||
          Array.from(dropdowns).every((select) => select.value !== "");
        const variantIsAvailable =
          !matchedVariant || Boolean(matchedVariant.available);

        const shouldDisable =
          !(swatchSelected && allDropdownsSelected) || !variantIsAvailable;

        this.addToCartButton.classList.toggle("disabled", shouldDisable);
      }

      // Updates the add-to-cart button's variant ID when a new variant is matched
      updateVariantId(variant) {
        if (!this.addToCartButton) return;
        this.addToCartButton.setAttribute("data-variant-id", variant.id);
        this.addToCartButton.dispatchEvent(new CustomEvent("change"));
      }

      // Replaces the primary card image src with the selected variant's featured image
      updateImage(variant) {
        if (!variant.featured_image || !this.primaryImage) return;

        this.primaryImage.src = variant.featured_image;
        this.primaryImage.srcset = "";
      }

      // Updates the price display with the selected variant's price and toggles the sale state
      updatePrice(variant) {
        if (!this.priceEl) return;

        const regularPriceItem = this.priceEl.querySelector(
          ".price__regular .price-item--regular",
        );
        const saleContainer = this.priceEl.querySelector(".price__sale");
        const saleRegularItem = saleContainer?.querySelector(
          ".price-item--regular",
        );
        const salePriceItem = saleContainer?.querySelector(".price-item--sale");

        // if (variant.compare_at_price) {
        //   this.priceEl.classList.add("price--on-sale");
        //   if (regularPriceItem) regularPriceItem.textContent = variant.price;
        //   if (saleRegularItem)
        //     saleRegularItem.textContent = variant.compare_at_price;
        //   if (salePriceItem) salePriceItem.textContent = variant.price;
        // } else {
        //   this.priceEl.classList.remove("price--on-sale");
        //   if (regularPriceItem) regularPriceItem.textContent = variant.price;
        //   if (saleRegularItem) saleRegularItem.textContent = "";
        //   if (salePriceItem) salePriceItem.textContent = "";
        // }
        if (variant.compare_at_price) {
          this.priceEl.classList.add("price--on-sale");
          if (regularPriceItem) regularPriceItem.innerHTML = variant.price;
          if (saleRegularItem)
            saleRegularItem.innerHTML = variant.compare_at_price;
          if (salePriceItem) salePriceItem.innerHTML = variant.price;
        } else {
          this.priceEl.classList.remove("price--on-sale");
          if (regularPriceItem) regularPriceItem.innerHTML = variant.price;
          if (saleRegularItem) saleRegularItem.innerHTML = "";
          if (salePriceItem) salePriceItem.innerHTML = "";
        }
      }

      // Initialises a Swiper carousel on mobile (<=749px) and destroys it on larger viewports
      initCarousel() {
        if (!this.carouselEl) return;

        const swiperParams = {
          slidesPerView: 1,
          loop: true,
          nested: true,
          touchStartPreventDefault: false,
          touchMoveStopPropagation: true,
          pagination: {
            el: this.carouselEl.querySelector(".swiper-pagination"),
            clickable: true,
          },
        };

        this.mql = window.matchMedia("(max-width: 749px)");

        const handleChange = (event) => {
          if (event.matches) {
            this.swiperInstance = new Swiper(this.carouselEl, swiperParams);
            this.syncNestedTouchState();
          } else if (this.swiperInstance) {
            this.destroyCarousel();
          }
        };

        this.mql.addEventListener("change", handleChange);

        if (this.mql.matches) {
          this.swiperInstance = new Swiper(this.carouselEl, swiperParams);
        }

        this.observeOuterSlideState();
      }

      // When the card sits inside an outer carousel slide, only the active
      // slide keeps its image swiper enabled — a swipe on a partially visible
      // card falls through and moves the outer carousel instead
      observeOuterSlideState() {
        this.outerSlideEl = this.closest(".swiper-slide");
        if (!this.outerSlideEl) return;

        this.syncNestedTouchState();

        this.outerSlideObserver = new MutationObserver(() =>
          this.syncNestedTouchState(),
        );
        this.outerSlideObserver.observe(this.outerSlideEl, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }

      syncNestedTouchState() {
        if (!this.swiperInstance || !this.outerSlideEl) return;

        if (this.outerSlideEl.classList.contains("swiper-slide-active")) {
          this.swiperInstance.enable();
        } else {
          this.swiperInstance.disable();
        }
      }

      disconnectedCallback() {
        this.outerSlideObserver?.disconnect();
      }

      destroyCarousel() {
        if (!this.swiperInstance) return;

        this.swiperInstance.destroy(true, true);
        this.swiperInstance = undefined;
      }

      // Parses and caches variant JSON data from the embedded script
      get variantsData() {
        if (!this._variantsData) {
          const el = this.querySelector("script[data-variants]");
          try {
            this._variantsData = JSON.parse(el.textContent);
          } catch {
            this._variantsData = [];
          }
        }
        return this._variantsData;
      }

      get swatchContainer() {
        return this.querySelector("[data-card-swatches]");
      }

      get primaryImage() {
        return (
          this.querySelector(".swiper-slide img") ||
          this.querySelector(".card-product__media img")
        );
      }

      get priceEl() {
        return this.querySelector("[data-variant-price]");
      }

      get dropdownContainer() {
        return this.querySelector("[data-card-dropdowns]");
      }

      get addToCartButton() {
        return this.querySelector("add-to-cart");
      }

      get carouselEl() {
        return this.querySelector(".card-product__media-carousel");
      }

      get productLinks() {
        return this.querySelectorAll("[data-product-link]");
      }

      get quickAddButton() {
        return this.querySelector(
          'quick-add[data-product-url]:not([data-mode="wishlist"])',
        );
      }

      get quickAddButtons() {
        return this.querySelectorAll("quick-add[data-product-url]");
      }
    },
  );
}
